import { createStore } from "@tellescope/store";

/** Applies arrayMove semantics to a Record<string, number> index map. */
const reorderIndexes = (
  indexes: Record<string, number>,
  fromIndex: number,
  toIndex: number
): Record<string, number> => {
  const updated = { ...indexes };

  if (fromIndex < toIndex) {
    // Moving down: shift items in (fromIndex, toIndex] up by 1
    Object.entries(updated).forEach(([key, idx]) => {
      if (idx > fromIndex && idx <= toIndex) {
        updated[key] = idx - 1;
      }
    });
  } else {
    // Moving up: shift items in [toIndex, fromIndex) down by 1
    Object.entries(updated).forEach(([key, idx]) => {
      if (idx >= toIndex && idx < fromIndex) {
        updated[key] = idx + 1;
      }
    });
  }

  return updated;
};

interface CallQueueStore {
  playSoundOnNewCall: boolean;
  setPlaySoundOnNewCall: (newVal: boolean) => void;
  queueIndexes: Record<string, number>;
  setQueueIndexes: (queueIndexes: Record<string, number>) => void;
  reorder: (activeId: string, overId: string) => void;
  moveToFront: (id: string) => void;
  moveToBack: (id: string) => void;
  removeFromQueue: (id: string) => void;
}

export const { useStore: useCallQueueStore, useShallowStore: useCallQueueShallowStore } = createStore<CallQueueStore>(
  (set) => ({
    playSoundOnNewCall: false,
    setPlaySoundOnNewCall: (newVal) => set({ playSoundOnNewCall: newVal }),
    queueIndexes: {},
    setQueueIndexes: (queueIndexes) => set({ queueIndexes }),
    reorder: (activeId, overId) =>
      set((state) => {
        if (activeId === overId) return state;

        const fromIndex = state.queueIndexes[activeId];
        const toIndex = state.queueIndexes[overId];
        if (fromIndex === undefined || toIndex === undefined) return state;

        const updated = reorderIndexes(state.queueIndexes, fromIndex, toIndex);
        updated[activeId] = toIndex;

        return { queueIndexes: updated };
      }),
    moveToFront: (id) =>
      set((state) => {
        const fromIndex = state.queueIndexes[id];
        if (fromIndex === undefined || fromIndex === 0) return state;

        const updated = reorderIndexes(state.queueIndexes, fromIndex, 0);
        updated[id] = 0;

        return { queueIndexes: updated };
      }),
    moveToBack: (id) =>
      set((state) => {
        const fromIndex = state.queueIndexes[id];
        if (fromIndex === undefined) return state;

        const maxIndex = Math.max(...Object.values(state.queueIndexes));
        if (fromIndex === maxIndex) return state;

        const updated = reorderIndexes(state.queueIndexes, fromIndex, maxIndex);
        updated[id] = maxIndex;

        return { queueIndexes: updated };
      }),
    removeFromQueue: (id) =>
      set((state) => {
        const removedIndex = state.queueIndexes[id];
        if (removedIndex === undefined) return state;

        const updated = { ...state.queueIndexes };
        delete updated[id];

        Object.entries(updated).forEach(([key, idx]) => {
          if (idx > removedIndex) {
            updated[key] = idx - 1;
          }
        });

        return { queueIndexes: updated };
      }),
  }),
  {
    persist: "localStorage",
    name: "CALL_HOLD_QUEUES_STORE",
  }
);
